import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type PaginationState,
  type SortingState,
  type Updater,
} from "@tanstack/react-table";
import {
  Badge,
  Button,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { DataTable } from "../DataTable";
import { ActivityDate } from "../ActivityDate";
import type { AppVersionDTO } from "../../utils/api";

interface AppVersionsTableProps {
  versions: AppVersionDTO[];
  totalVersions: number;
  // eslint-disable-next-line no-unused-vars
  onEdit?: (version: AppVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (version: AppVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onPublish?: (version: AppVersionDTO) => void;
  onTableChange: (
    // eslint-disable-next-line no-unused-vars
    pagination: Updater<PaginationState>,
    // eslint-disable-next-line no-unused-vars
    sorting: Updater<SortingState>
  ) => void;
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDescending?: boolean;
  showActions?: boolean;
}

interface AppVersionsTableMeta {
  // eslint-disable-next-line no-unused-vars
  onEdit?: (version: AppVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (version: AppVersionDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onPublish?: (version: AppVersionDTO) => void;
  latestPublishedVersionCode?: number;
}

const actionsColumn: ColumnDef<AppVersionDTO, unknown> = {
  id: "actions",
  header: "Actions",
  enableSorting: false,
  enableResizing: true,
  cell: ({ row, table }) => {
    const version = row.original;
    const meta = table.options.meta as AppVersionsTableMeta;
    return (
      <ButtonGroup
        className="d-block mx-auto"
        style={{
          marginTop: "-6px",
          marginBottom: "-6px",
        }}
      >
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`edit-version-${version.id}`}>Edit</Tooltip>}
        >
          <span className="d-inline-block">
            <Button
              variant="outline-dark"
              size="sm"
              aria-label="Edit version"
              onClick={() => meta?.onEdit?.(version)}
            >
              <FontAwesomeIcon width={20} icon={faPencil} />
            </Button>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`publish-version-${version.id}`}>Publish</Tooltip>
          }
        >
          <span className="d-inline-block">
            <Button
              variant="outline-dark"
              size="sm"
              aria-label="Publish version"
              disabled={version.status === "PUBLISHED" || version.isDebug}
              style={
                version.status === "PUBLISHED" || version.isDebug
                  ? { pointerEvents: "none" }
                  : undefined
              }
              onClick={() => meta?.onPublish?.(version)}
            >
              <FontAwesomeIcon width={20} icon={faUpload} />
            </Button>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`delete-version-${version.id}`}>Delete</Tooltip>
          }
        >
          <span className="d-inline-block">
            <Button
              variant="outline-dark"
              size="sm"
              aria-label="Delete version"
              onClick={() => meta?.onDelete?.(version)}
            >
              <FontAwesomeIcon width={20} icon={faTrash} />
            </Button>
          </span>
        </OverlayTrigger>
      </ButtonGroup>
    );
  },
  meta: {
    width: "min-content",
  },
};

const baseColumns: ColumnDef<AppVersionDTO, unknown>[] = [
  {
    accessorKey: "versionName",
    header: () => "Version Name",
    cell: ({ row, table }) => {
      const version = row.original;
      const meta = table.options.meta as AppVersionsTableMeta;
      const isCurrentPublishedVersion =
        version.status === "PUBLISHED" &&
        !version.isDebug &&
        version.versionCode === meta?.latestPublishedVersionCode;
      return (
        <div className="d-flex align-items-center gap-2">
          <span>{version.versionName}</span>
          {isCurrentPublishedVersion ? (
            <Badge bg="success">Current</Badge>
          ) : null}
        </div>
      );
    },
    minSize: 120,
  },
  {
    accessorKey: "versionCode",
    header: () => "Version Code",
    minSize: 90,
    maxSize: 120,
  },
  {
    accessorKey: "isDebug",
    header: () => "Build Type",
    cell: (info) => (
      <Badge bg={info.getValue<boolean>() ? "warning" : "success"}>
        {info.getValue<boolean>() ? "Debug" : "Release"}
      </Badge>
    ),
    minSize: 100,
    maxSize: 120,
  },
  {
    accessorKey: "status",
    header: () => "Status",
    minSize: 100,
    maxSize: 130,
  },
  {
    accessorKey: "forceUpdate",
    header: () => "Force Update",
    cell: (info) => (info.getValue<boolean>() ? "Yes" : "No"),
    minSize: 100,
    maxSize: 120,
  },
  {
    accessorKey: "downloadUrl",
    header: () => "APK file",
    cell: (info) => (
      <a href={info.getValue<string>()} target="_blank" rel="noreferrer">
        {info.getValue<string>()}
      </a>
    ),
    minSize: 220,
  },
  {
    accessorKey: "releaseNotes",
    header: () => "Release Notes",
    minSize: 180,
  },
  {
    accessorKey: "sha256",
    header: () => "SHA256",
    cell: (info) => <code>{info.getValue<string>()}</code>,
    minSize: 220,
  },
  {
    accessorKey: "createdAt",
    header: () => "Created",
    cell: (info) => <ActivityDate date={info.getValue<string>()} />,
    minSize: 120,
    maxSize: 150,
  },
];

export function AppVersionsTable({
  versions,
  totalVersions,
  onEdit,
  onDelete,
  onPublish,
  onTableChange,
  pageIndex,
  pageSize,
  sortBy,
  sortDescending,
  showActions = true,
}: AppVersionsTableProps): React.ReactNode {
  const columns = useMemo(
    () => (showActions ? [...baseColumns, actionsColumn] : baseColumns),
    [showActions]
  );

  const latestPublishedVersionCode = versions
    .filter((version) => version.status === "PUBLISHED" && !version.isDebug)
    .reduce<
      number | undefined
    >((latestCode, version) => (latestCode === undefined || version.versionCode > latestCode ? version.versionCode : latestCode), undefined);

  const table = useReactTable<AppVersionDTO>({
    data: versions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: (pagination) => {
      onTableChange(pagination, (prev) => prev);
    },
    onSortingChange: (sorting) => {
      onTableChange((prev) => ({ ...prev, pageIndex: 0 }), sorting);
    },
    enableColumnResizing: true,
    meta: {
      onEdit,
      onDelete,
      onPublish,
      latestPublishedVersionCode,
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: totalVersions,
    state: {
      pagination: { pageIndex, pageSize },
      sorting: sortBy
        ? [
            {
              id: sortBy,
              desc: sortDescending || false,
            },
          ]
        : [],
    },
    defaultColumn: {
      size: Number.NaN,
      minSize: Number.NaN,
      maxSize: Number.NaN,
    },
  });

  return <DataTable table={table} itemName="version" />;
}
